import { useEffect, useState } from "react";
import { History, TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";
import { apiMyAnalyses } from "@/lib/api";

/**
 * MarketHistoryPanel — compact list of the user's recent LIVE MARKET analyses.
 *
 * Scoped intentionally to `source=market` so the coexistence UX stays
 * clean: everything shown here is inline-openable on THIS page. Screenshot
 * analyses live at /analyze and are not surfaced here (they would be
 * confusing — clicking a screenshot row does not reopen it inline).
 *
 * Selecting a row calls `onSelect(row)` — the parent renders it via
 * `ResultsView`.
 */
function biasIcon(bias) {
  if (bias === "BULLISH") return <TrendingUp className="w-3.5 h-3.5 text-[#00FF66]" />;
  if (bias === "BEARISH") return <TrendingDown className="w-3.5 h-3.5 text-[#FF3B30]" />;
  return <Minus className="w-3.5 h-3.5 text-[#FFB000]" />;
}

function statusPill(status) {
  if (status === "READY" || status === "LONG READY") return { fg: "#00FF66", text: "READY" };
  if (status === "SHORT READY") return { fg: "#FF3B30", text: "SHORT READY" };
  if (status === "WAITING FOR ENTRY") return { fg: "#FFB000", text: "WAIT · ENTRY" };
  if (status === "WAITING FOR CONFIRMATION" || status === "WAIT FOR CONFIRMATION") return { fg: "#FFB000", text: "WAIT · CONFIRM" };
  return { fg: "#A0A0A0", text: "NO TRADE" };
}

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function MarketHistoryPanel({ onSelect, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Only market rows — coexistence rule: history on the Market
        // page must be re-openable inline. Screenshot rows would break
        // that contract.
        const data = await apiMyAnalyses({ source: "market", limit: 12 });
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  return (
    <aside
      className="border border-neutral-900 bg-[#0a0a0a] p-4"
      data-testid="market-history-panel"
    >
      <div className="flex items-center gap-2 mb-1">
        <History className="w-3.5 h-3.5 text-[#00E5FF]" />
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00E5FF]">
          your live market history
        </div>
      </div>
      <p className="text-[10px] text-neutral-600 leading-relaxed mb-3">
        Screenshot analyses live on the Analyze page.
      </p>

      {loading && (
        <div className="text-xs font-mono text-neutral-500" data-testid="history-loading">
          loading…
        </div>
      )}
      {error && !loading && (
        <div className="text-xs font-mono text-[#FF3B30]" data-testid="history-error">
          {error}
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div
          className="text-xs text-neutral-500 leading-relaxed"
          data-testid="history-empty"
        >
          No live market analyses yet. Run one to see it here.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <ul className="space-y-1.5" data-testid="history-list">
          {rows.map((r, i) => {
            const s = statusPill(r.setup_status);
            const label = `${r?.market_context?.symbol || r.asset_guess || "market"} · ${r?.market_context?.timeframe || r.timeframe_guess || ""}`;
            const clickable = !!onSelect;
            return (
              <li key={r.id || i}>
                <button
                  type="button"
                  onClick={clickable ? () => onSelect(r) : undefined}
                  disabled={!clickable}
                  data-testid={`history-row-${i}`}
                  className={
                    "w-full text-left border border-neutral-900 bg-black/40 px-3 py-2.5 " +
                    "flex flex-col gap-1.5 " +
                    (clickable
                      ? "hover:border-[#00E5FF]/50 hover:bg-[#00E5FF]/[0.04] transition-colors cursor-pointer"
                      : "opacity-70 cursor-default")
                  }
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] border text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/10">
                      <Activity className="w-3 h-3" />
                      LIVE
                    </span>
                    <span className="text-[10px] font-mono text-neutral-500">
                      {fmtWhen(r.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {biasIcon(r.market_bias)}
                      <span className="font-mono text-xs text-white truncate">{label}</span>
                    </div>
                    <span
                      className="font-mono text-[10px] uppercase tracking-[0.15em] flex-shrink-0"
                      style={{ color: s.fg }}
                    >
                      {s.text}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}
